import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db6e9nbghk82orf0ckhg";

const scopes = [
  { icon: "ri-newspaper-line", title: "韩剧资讯", desc: "新剧开播、演员动态、行业观察，第一时间跟进。" },
  { icon: "ri-film-line", title: "剧集推荐", desc: "按题材与口碑精选作品，帮你快速决定看什么。" },
  { icon: "ri-compass-3-line", title: "剧情解析", desc: "还原伏笔与细节，带你读懂作品背后的表达。" },
  { icon: "ri-stack-line", title: "专题合集", desc: "主题片单一站式整理，跟着看系列不迷路。" },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 韩剧TV",
    description:
      "韩剧TV 是专注韩剧资讯与剧集推荐的内容平台，提供最新韩剧动态、热门剧集介绍、剧情解析与观看指南，欢迎通过页面表单与我们联系。",
    keywords: "关于韩剧TV,韩剧资讯平台,韩剧推荐网站,韩剧TV联系",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string") payload.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg && !isSpam && parsed?.code !== "OK"
            ? serverMsg
            : "提交失败，请稍后重试，或稍后再联系我们。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      {/* 定位 */}
      <section className="mt-6 grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
        <div>
          <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-500 text-primary-700">
            <i className="ri-heart-3-line"></i> 关于韩剧TV
          </span>
          <h1 className="mt-4 font-heading text-3xl font-800 text-foreground-950 md:text-4xl">
            让追剧更简单，让好剧不被错过
          </h1>
          <p className="mt-5 text-base leading-8 text-foreground-700">
            韩剧TV 是一个专注韩剧资讯与剧集推荐的内容平台。我们面向各年龄层的韩剧爱好者，
            也欢迎任何想了解韩剧动态的观众。无论是刚入坑的新人，还是追剧多年的资深粉丝，
            都能在这里找到属于自己的观剧指南。
          </p>
          <p className="mt-4 text-base leading-8 text-foreground-700">
            我们坚持内容的专业与及时：每日整理最新资讯，为每部推荐剧集提供清晰的剧情介绍与看点梳理，
            并持续制作主题专题，帮助你在海量内容中高效做出选择。
          </p>
          <div className="mt-6 grid grid-cols-3 gap-4">
            {[
              { v: "每日", l: "资讯更新" },
              { v: "12+", l: "精选剧集" },
              { v: "4", l: "主题专题" },
            ].map((s) => (
              <div key={s.l} className="rounded-xl bg-background-100 px-4 py-5 text-center">
                <p className="font-heading text-xl font-800 text-primary-600 md:text-2xl">{s.v}</p>
                <p className="mt-1 text-xs text-foreground-500">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl bg-background-200">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cozy%20modern%20living%20room%20with%20a%20large%20screen%20and%20popcorn%2C%20soft%20pink%20and%20cream%20tones%2C%20inviting%20editorial%20atmosphere&width=1200&height=900&seq=about-hero-01&orientation=landscape"
            alt="韩剧TV 内容团队工作场景"
            title="关于韩剧TV"
            className="h-full w-full object-cover object-top"
          />
        </div>
      </section>

      {/* 内容范围 */}
      <section className="mt-16">
        <h2 className="font-heading text-2xl font-700 text-foreground-950 md:text-3xl">我们的内容范围</h2>
        <p className="mt-2 text-sm text-foreground-600">围绕韩剧资讯与观剧体验，我们提供以下内容。</p>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {scopes.map((s) => (
            <div key={s.title} className="rounded-xl border border-background-200/70 bg-background-50 p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${s.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-600 text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 联系方式 + 表单 */}
      <section className="mt-16 rounded-2xl bg-background-100 p-6 md:p-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <h2 className="font-heading text-2xl font-700 text-foreground-950 md:text-3xl">联系我们</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              有内容建议、合作意向或使用问题？填写右侧表单，我们会尽快回复。你也可以通过以下方式找到我们。
            </p>
            <ul className="mt-6 space-y-4 text-sm text-foreground-700">
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-50 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <span>
                  <span className="block font-500 text-foreground-900">邮箱</span>
                  hello@hanju-tv.example.com
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-50 text-primary-600">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <span>
                  <span className="block font-500 text-foreground-900">内容更新</span>
                  每日更新，欢迎常回来看看
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-50 text-primary-600">
                  <i className="ri-map-pin-line text-lg"></i>
                </span>
                <span>
                  <span className="block font-500 text-foreground-900">线上平台</span>
                  首尔 · 上海 · 全球在线
                </span>
              </li>
            </ul>
          </div>

          <div className="rounded-xl border border-background-200/70 bg-background-50 p-6">
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-checkbox-circle-line text-3xl"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-700 text-foreground-950">提交成功</h3>
                <p className="mt-2 max-w-sm text-sm text-foreground-600">
                  感谢你的留言，我们已经收到，会尽快回复你。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                >
                  再写一条
                </button>
              </div>
            ) : (
              <form
                id="about-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-sm font-500 text-foreground-800">
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-sm font-500 text-foreground-800">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收我们的回复"
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="mb-1.5 block text-sm font-500 text-foreground-800">
                    联系类型
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    required
                    defaultValue=""
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="" disabled>请选择联系类型</option>
                    <option value="内容建议">内容建议</option>
                    <option value="剧集推荐">剧集推荐</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="使用问题">使用问题</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-1.5 block text-sm font-500 text-foreground-800">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请简单描述你的问题或建议（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="aux-offset-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && (
                  <p className="flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                    <i className="ri-error-warning-line"></i> {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i> 提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i> 提交留言
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* 版权声明与使用须知 */}
      <section className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="rounded-xl border border-background-200/70 bg-background-50 p-6">
          <h2 className="flex items-center gap-2 font-heading text-lg font-700 text-foreground-950">
            <i className="ri-copyright-line text-primary-500"></i> 版权声明
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            本站所有剧集名称、人物设定、图片与资讯内容均为虚构示例数据，仅用于产品功能演示，
            不涉及任何真实影视作品或版权信息。若内容与真实作品雷同，纯属巧合。
          </p>
        </div>
        <div className="rounded-xl border border-background-200/70 bg-background-50 p-6">
          <h2 className="flex items-center gap-2 font-heading text-lg font-700 text-foreground-950">
            <i className="ri-information-line text-accent-500"></i> 使用须知
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            本站为前端演示站点，搜索、筛选与收藏功能均在本地浏览器完成，收藏数据仅保存在你自己的设备中，
            不会上传服务器，也无需注册账户。清除浏览器数据后收藏记录将被重置。
          </p>
        </div>
      </section>

      <div className="mt-10 flex justify-center">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-500 text-foreground-700 hover:bg-background-100 cursor-pointer">
          <i className="ri-arrow-left-line"></i> 返回首页
        </Link>
      </div>
    </div>
  );
}